/** Dispatched on `window` when a new service worker is waiting or has taken control. */
export const SW_UPDATE_EVENT = 'platzona-sw-update';

export type WatchServiceWorkerUpdatesOptions = {
  onUpdate?: () => void;
};

function postSkipWaiting(worker: ServiceWorker | null | undefined): void {
  if (!worker) return;
  try {
    worker.postMessage({ type: 'SKIP_WAITING' });
  } catch {
    /* SW may not listen — controllerchange / reload prompt still covers activate */
  }
}

/**
 * Watch for a waiting or newly controlling service worker.
 * Calls `onUpdate` at most once. Returns an unsubscribe function.
 *
 * Prefer pairing with a soft “Refresh” banner; do not force reload.
 */
export function watchServiceWorkerUpdates(
  opts?: WatchServiceWorkerUpdatesOptions,
): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return () => undefined;
  }

  let fired = false;
  let cancelled = false;
  let removeUpdateFound: (() => void) | undefined;

  const fire = () => {
    if (fired || cancelled) return;
    fired = true;
    opts?.onUpdate?.();
  };

  const onControllerChange = () => fire();
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  const attachRegistration = (reg: ServiceWorkerRegistration) => {
    if (reg.waiting) {
      postSkipWaiting(reg.waiting);
      fire();
    }

    const onUpdateFound = () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state !== 'installed') return;
        if (reg.waiting) postSkipWaiting(reg.waiting);
        // Only prompt when there was already a controller (an update, not first install).
        if (navigator.serviceWorker.controller) fire();
      });
    };
    reg.addEventListener('updatefound', onUpdateFound);
    removeUpdateFound = () => reg.removeEventListener('updatefound', onUpdateFound);

    void reg
      .update()
      .then(() => {
        if (cancelled) return;
        if (reg.waiting) {
          postSkipWaiting(reg.waiting);
          fire();
        }
      })
      .catch(() => {
        /* progressive enhancement */
      });
  };

  void navigator.serviceWorker.getRegistration().then((reg) => {
    if (cancelled || !reg) return;
    attachRegistration(reg);
  });

  return () => {
    cancelled = true;
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    removeUpdateFound?.();
  };
}

/**
 * Starts SW update watching and dispatches `platzona-sw-update` when ready.
 * Optional `onUpdate` also subscribes to that event (or is called directly when `window` is unavailable).
 * Returns unsubscribe (stops watching + removes the callback).
 */
export function promptServiceWorkerUpdate(onUpdate?: () => void): () => void {
  const target = typeof window !== 'undefined' ? window : undefined;

  // Node / SSR: skip CustomEvent bus; still honor the callback via the watch path.
  if (!target) {
    return watchServiceWorkerUpdates({ onUpdate });
  }

  const handler = () => onUpdate?.();
  if (onUpdate) {
    target.addEventListener(SW_UPDATE_EVENT, handler);
  }

  const stopWatch = watchServiceWorkerUpdates({
    onUpdate: () => {
      target.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT));
    },
  });

  return () => {
    stopWatch();
    if (onUpdate) target.removeEventListener(SW_UPDATE_EVENT, handler);
  };
}

export type PwaShellUpdateResult = 'reloaded' | 'unsupported';

/**
 * Check for a new service worker, activate it, then hard-reload the shell.
 * Used by pull-to-update and the “Update now” banner so phones drop stale caches.
 */
export async function requestPwaShellUpdate(): Promise<PwaShellUpdateResult> {
  if (typeof window === 'undefined') return 'unsupported';

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        if (reg.waiting) postSkipWaiting(reg.waiting);
        // Give activate/claim a beat before navigation.
        await new Promise((r) => window.setTimeout(r, 350));
      }
    } catch {
      /* still reload — network HTML is no-cache */
    }
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_pv', String(Date.now()));
  window.location.replace(`${url.pathname}${url.search}${url.hash}`);
  return 'reloaded';
}

/** Re-check SW when the PWA returns to the foreground. */
export function watchPwaUpdateOnVisible(): () => void {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') {
    return () => undefined;
  }
  if (!('serviceWorker' in navigator)) return () => undefined;

  const onVis = () => {
    if (document.visibilityState !== 'visible') return;
    void navigator.serviceWorker.getRegistration().then((reg) => {
      void reg?.update().catch(() => undefined);
    });
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

/**
 * Register `/sw.js`, check for updates immediately, and reload once when a new
 * worker takes control so Freigabe phones do not stay on a stale hashed shell.
 */
export function registerPlatzonaServiceWorker(
  scriptUrl = '/sw.js',
  opts?: { /** True while a reload would hurt (a running game, a call): the reload waits until it is false. */ deferReload?: () => boolean },
): void {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Only auto-reload on *updates* (there was already a controlling worker).
  // Session cap: one controllerchange reload — fights installPwaForceUpdate / HTML guard
  // so Freigabe never blinks when several helpers skipWaiting in the same boot.
  const hadController = Boolean(navigator.serviceWorker.controller);
  let refreshing = false;
  const reloadDoneKey = 'platzona-sw-controller-reload:done';
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing || !hadController) return;
    try {
      if (sessionStorage.getItem(reloadDoneKey) === '1') return;
      sessionStorage.setItem(reloadDoneKey, '1');
    } catch {
      /* private mode — still allow a single in-memory reload */
    }
    refreshing = true;
    const deferReload = opts?.deferReload;
    if (!deferReload?.()) {
      window.location.reload();
      return;
    }
    const timer = window.setInterval(() => {
      if (deferReload()) return;
      window.clearInterval(timer);
      window.location.reload();
    }, 2000);
  });

  void navigator.serviceWorker
    .register(scriptUrl)
    .then((reg) => {
      void reg.update().catch(() => undefined);
      if (reg.waiting) postSkipWaiting(reg.waiting);
      // Periodic check while the Home Screen app stays open.
      window.setInterval(() => {
        void reg.update().catch(() => undefined);
      }, 60_000);
    })
    .catch(() => {
      /* progressive enhancement */
    });

  watchPwaUpdateOnVisible();
}
